import { imgUrl } from "@/lib/api";
import { useLocale } from "@/context/LocaleContext";

export function pct(rate) {
  if (rate == null) return "—";
  return `${(Number(rate) * 100).toFixed(1)}%`;
}

export function useDisplayMoney() {
  const locale = useLocale();
  return (value, currency = "USD") => {
    if (value == null || value === "") return "—";
    const amount = typeof value === "object" ? value.amount : value;
    const code = typeof value === "object" ? (value.currency || currency) : currency;
    if (amount == null || amount === "" || Number.isNaN(Number(amount))) return "—";
    if (locale?.formatMoney) return locale.formatMoney(Number(amount), code || "USD");
    return `${code || "USD"} ${Number(amount)}`;
  };
}

export function amount(value, currency = "USD") {
  if (value == null) return "—";
  return `${currency} ${Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function Kicker({ children, className = "" }) {
  return <div className={`text-[11px] tracking-luxe uppercase text-aura-muted ${className}`}>{children}</div>;
}

export function PageHeader({ title, children, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="font-serif text-3xl tracking-tight">{title}</h1>
        {children && <p className="text-sm text-aura-muted mt-1 max-w-2xl">{children}</p>}
      </div>
      {action}
    </div>
  );
}

export function Empty({ children, testId }) {
  return <div className="border border-aura-border px-5 py-8 text-sm text-aura-muted" data-testid={testId}>{children}</div>;
}

export const inputCls = "bg-aura-surface border border-aura-border px-4 py-2.5 text-sm outline-none focus:border-aura-accent";
export const btnCls = "px-5 py-2.5 btn-accent text-sm font-medium rounded-full inline-flex items-center gap-2 disabled:opacity-60";
export const ghostCls = "px-4 py-2 border border-aura-border text-sm rounded-full text-aura-muted hover:text-aura-ink inline-flex items-center gap-2 disabled:opacity-60";

export function Table({ title, empty, head, rows, testId }) {
  return (
    <section data-testid={testId}>
      {title && <Kicker className="mb-3">{title}</Kicker>}
      {rows.length === 0 ? (
        <div className="border border-aura-border px-5 py-6 text-sm text-aura-muted">{empty}</div>
      ) : (
        <div className="border border-aura-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] tracking-luxe uppercase text-aura-muted border-b border-aura-border">
                {head.map((h, i) => (
                  <th key={h} className={`px-4 py-2.5 font-normal ${i === 0 ? "text-left" : "text-right"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-aura-border">
              {rows.map((cells, r) => (
                <tr key={r}>
                  {cells.map((c, i) => (
                    <td key={i} className={`px-4 py-2.5 ${i === 0 ? "text-left max-w-[260px] truncate" : "text-right font-mono text-aura-muted whitespace-nowrap"}`}>
                      {c}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export function Stat({ label, value, hint, testId }) {
  return (
    <div className="border border-aura-border p-4" data-testid={testId}>
      <Kicker>{label}</Kicker>
      <div className="font-serif text-2xl mt-1">{value}</div>
      {hint && <div className="text-[11px] text-aura-muted mt-1">{hint}</div>}
    </div>
  );
}

const MARKET_LABELS = {
  ebay: "eBay",
  etsy: "Etsy",
  amazon: "Amazon",
  walmart: "Walmart",
  target: "Target",
  poshmark: "Poshmark",
  mercari: "Mercari",
  offerup: "OfferUp",
  reverb: "Reverb",
  craigslist: "Craigslist",
  chairish: "Chairish",
  wayfair: "Wayfair",
  vinted: "Vinted",
};

function marketLabel(offer, product) {
  const key = String(offer?.marketplace || product?.marketplace || "").toLowerCase();
  if (key && MARKET_LABELS[key]) return MARKET_LABELS[key];
  if (key) return key;
  const source = String(offer?.source || product?.source || "").toLowerCase();
  if (source === "amazon_associates") return "Amazon";
  if (source === "walmart_affiliate") return "Walmart";
  if (source === "crawlee") return "";
  if (MARKET_LABELS[source]) return MARKET_LABELS[source];
  return "";
}

export function OfferRow({ product, action }) {
  const show = useDisplayMoney();
  const rawImg = product.images?.[0];
  const img = typeof rawImg === "string" ? rawImg : rawImg?.url;
  const primary = (product.offers || [])[0];
  const store = marketLabel(primary, product);
  return (
    <div className="border border-aura-border p-4" data-marketplace={store || undefined}>
      <div className="flex gap-3">
        <div className="w-14 h-14 shrink-0 bg-aura-sand flex items-center justify-center overflow-hidden">
          {img ? (
            <img
              referrerPolicy="no-referrer"
              src={imgUrl(img)}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <span className="text-[10px] text-aura-muted px-1 text-center leading-tight">No image</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          {store && (
            <div className="text-[11px] tracking-luxe uppercase text-aura-accent mb-0.5" data-testid="offer-marketplace">
              {store}
            </div>
          )}
          <div className="text-sm font-medium truncate">{product.title}</div>
          <div className="text-[11px] text-aura-muted mt-0.5">
            {product.offer_count} offer{product.offer_count === 1 ? "" : "s"}
          </div>
          <div className="mt-2 space-y-1">
            {(product.offers || []).slice(0, 4).map((o) => {
              const market = marketLabel(o, product);
              const seller = o.merchant && o.merchant.toLowerCase() !== market.toLowerCase() ? o.merchant : "";
              const label = [market, seller].filter(Boolean).join(" · ") || "Store";
              return (
                <div key={o.id} className="flex justify-between gap-3 text-xs">
                  <a href={o.buy_url} target="_blank" rel="noreferrer" className="truncate link-underline">{label}</a>
                  <span className="font-mono shrink-0">
                    {o.price?.amount == null ? "—" : show(o.price)}
                    {o.commission_rate != null ? ` · ${pct(o.commission_rate)}` : ""}
                  </span>
                </div>
              );
            })}
          </div>
          {action && <div className="mt-3">{action}</div>}
        </div>
      </div>
    </div>
  );
}
